/**
 * Shared GA4 event helpers for ThatClicked conversion tracking.
 * Covers the full path: discovery → email signup → lesson start → lesson complete → confidence stage.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function sendGAEvent(eventName: string, params: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    window.gtag('event', eventName, params);
  }
}

// ─── email_submitted ──────────────────────────────────────────────────────────
// Fire when a user successfully submits their email address.
export function trackEmailSubmitted(params: {
  source: string;       // e.g. 'blog', 'landing_hero', 'final_cta'
  source_slug?: string; // page slug where the form lives
}): void {
  sendGAEvent('email_submitted', params);
}

// ─── lesson_started ───────────────────────────────────────────────────────────
// Fire when a user navigates to / activates a lesson.
export function trackLessonStarted(params: {
  lesson_id: string;    // e.g. 'free-1', 'o1', 'lesson-1-1'
  module_id: string;    // e.g. 'free', 'orientation', 'module-1'
  lesson_title?: string;
}): void {
  sendGAEvent('lesson_started', params);
}

// ─── lesson_completed ─────────────────────────────────────────────────────────
// Fire when a user marks a lesson as complete (clicks Next / completes the lesson).
export function trackLessonCompleted(params: {
  lesson_id: string;
  module_id: string;
  lesson_title?: string;
}): void {
  sendGAEvent('lesson_completed', params);
}

// ─── confidence_stage_updated ─────────────────────────────────────────────────
// Fire when the user's inferred Confidence Curve stage changes based on
// the number of modules they have completed.
// Stages map to Anna's Confidence Curve:
//   0 modules  → Fear
//   1–2 modules → Curiosity
//   3–4 modules → Experimentation
//   5–7 modules → Comfort
//   8–9 modules → Confidence
export function trackConfidenceStageUpdated(params: {
  stage: 'Fear' | 'Curiosity' | 'Experimentation' | 'Comfort' | 'Confidence';
  modules_completed: number;
  trigger?: string; // e.g. 'module_complete', 'orientation_complete'
}): void {
  sendGAEvent('confidence_stage_updated', params);
}

// ─── blog_signup_submitted ────────────────────────────────────────────────────
// Fire when a user successfully submits the blog email signup form.
// Captures which article drove the signup so enrollment attribution is possible.
export function trackBlogSignupSubmitted(params: {
  article_slug: string;   // slug of the blog post containing the form
  article_title: string;  // title of the blog post
}): void {
  sendGAEvent('blog_signup_submitted', {
    ...params,
    source: 'blog_signup_block',
  });
}

// ─── learn_hub_module_click ───────────────────────────────────────────────────
// Fire when a user clicks a paid-module or course CTA link inside the Learn Hub.
// Captures which article/section drove the click so enrollment attribution is possible.
export function trackLearnHubModuleClick(params: {
  article_slug: string;   // slug/identifier of the Learn Hub section or article
  article_title: string;  // human-readable title of the card clicked
  module_href: string;    // destination href (e.g. '/conversion-page', '/module/1')
}): void {
  sendGAEvent('learn_hub_module_click', params);
}

// ─── hero_section_view ────────────────────────────────────────────────────────
// Fire when the hero section becomes visible on page load.
// Used to measure impression volume after tone corrections (Copilot positioning
// update, generational language removal, testimonial removal — August 2026).
export function trackHeroSectionView(params: {
  page_context: 'homepage' | 'landing_page'; // which page the hero is on
  content_version?: string;                  // e.g. 'aug2026_tone_corrections'
}): void {
  sendGAEvent('hero_section_view', {
    ...params,
    content_version: params.content_version ?? 'aug2026_tone_corrections',
  });
}

// ─── hero_cta_click ───────────────────────────────────────────────────────────
// Fire when a user clicks a CTA button inside the hero section.
// Tracks which CTA drove the action and from which page, so post-correction
// engagement rates can be compared against pre-correction baselines.
export function trackHeroCTAClick(params: {
  cta_label: string;                         // e.g. 'start_free', 'download_cheat_sheet'
  page_context: 'homepage' | 'landing_page';
  content_version?: string;
}): void {
  sendGAEvent('hero_cta_click', {
    ...params,
    content_version: params.content_version ?? 'aug2026_tone_corrections',
  });
}

/**
 * Derive the Confidence Curve stage from the number of completed modules.
 */
export function getConfidenceStage(
  modulesCompleted: number
): 'Fear' | 'Curiosity' | 'Experimentation' | 'Comfort' | 'Confidence' {
  if (modulesCompleted >= 8) return 'Confidence';
  if (modulesCompleted >= 5) return 'Comfort';
  if (modulesCompleted >= 3) return 'Experimentation';
  if (modulesCompleted >= 1) return 'Curiosity';
  return 'Fear';
}
